import React, { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  Camera,
  CheckCircle2,
  Lightbulb,
  Loader2,
  Ruler,
  Sun,
  Move,
  RotateCcw,
  Video,
  X,
} from "lucide-react";
import { Exterior360VideoCaptureView } from "./Exterior360VideoCaptureView";
import { Exterior360VideoTrimView } from "./Exterior360VideoTrimView";
import {
  Exterior360SegmentPicker,
  type ImageByIndex,
} from "./Exterior360SegmentPicker";
import { Exterior360CameraView } from "./Exterior360CameraView";
import type { UploadQueueItem } from "@/hooks/useExterior360UploadQueue";
import { useTest } from "@/hooks/useTest";
import { CarTestService } from "@/services/carTest/carTestService";
import { useLanguage } from "@/context/LanguageContext";
import { exterior360Labels } from "@/i18n/labels/exterior360Labels";

const FRAMES_TOTAL = 72;

type Phase =
  | "guide"
  | "video"
  | "trim"
  | "picker"
  | "camera";

export interface Exterior360UploadQueueProps {
  enqueue: (frameIndex: number, viewType: "side", blob: Blob) => void;
  doneCount: number;
  failedCount: number;
  allDone: boolean;
  total: number;
  queue: UploadQueueItem[];
}

interface Exterior360CaptureModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  carTestId: string | null;
  initialFrameIndex?: number | null;
  /** Called when all uploads complete – parent can refresh its data */
  onUploadsComplete?: () => void;
  /** Shared upload queue – must be passed from parent so progress shows over pie when modal closes */
  uploadQueue: Exterior360UploadQueueProps;
}

export function Exterior360CaptureModal({
  open,
  onOpenChange,
  carTestId,
  initialFrameIndex = null,
  onUploadsComplete,
  uploadQueue,
}: Exterior360CaptureModalProps) {
  const { language } = useLanguage();
  const labels = exterior360Labels(language);
  const [phase, setPhase] = useState<Phase>("guide");
  const [recordedVideoBlob, setRecordedVideoBlob] = useState<Blob | null>(null);
  const [currentFrameIndex, setCurrentFrameIndex] = useState(0);
  const [imagesByIndex, setImagesByIndex] = useState<Record<number, ImageByIndex>>({});
  const [fullscreenImage, setFullscreenImage] = useState<{
    url: string;
    index: number;
  } | null>(null);
  const [deletingFrameIndex, setDeletingFrameIndex] = useState<number | null>(null);

  const {
    enqueue,
    doneCount,
    failedCount,
    allDone,
    total: uploadTotal,
    queue,
  } = uploadQueue;
  const { removeCarTestImage } = useTest({
    disableAdminQuery: true,
    disableUserQuery: true,
  });
  const uploadingFrameIndices = React.useMemo(
    () =>
      queue
        .filter(
          (i) =>
            i.status === "pending" ||
            i.status === "compressing" ||
            i.status === "uploading",
        )
        .map((i) => i.frameIndex),
    [queue],
  );

  // Sync imagesByIndex when uploads complete – update placeholder with server id/url
  const processedDoneIdsRef = React.useRef<Set<string>>(new Set());
  React.useEffect(() => {
    queue
      .filter(
        (i) =>
          i.status === "done" &&
          i.resultId != null &&
          i.resultUrl != null &&
          !processedDoneIdsRef.current.has(i.id),
      )
      .forEach((item) => {
        processedDoneIdsRef.current.add(item.id);
        setImagesByIndex((prev) => ({
          ...prev,
          [item.frameIndex]: {
            id: item.resultId!,
            url: item.resultUrl!,
            urlFull: item.resultUrlFull ?? item.resultUrl,
          },
        }));
      });
  }, [queue]);

  useEffect(() => {
    if (!open || !carTestId) return;
    if (
      initialFrameIndex != null &&
      initialFrameIndex >= 0 &&
      initialFrameIndex < FRAMES_TOTAL
    ) {
      setCurrentFrameIndex(initialFrameIndex);
      setPhase("camera");
    }
    Promise.all([
      CarTestService.getCarTestImages(carTestId, "EXTERIOR", true),
      CarTestService.getCarTestImages(carTestId, "EXTERIOR", false),
    ])
      .then(([previewImages, fullsizeImages]) => {
        const byIndex: Record<number, ImageByIndex> = {};
        previewImages
          .filter((i) => i.frameIndex != null)
          .forEach((i) => {
            const idx = i.frameIndex!;
            if (idx >= 0 && idx < FRAMES_TOTAL && i.url) {
              const full = fullsizeImages.find((f) => f.id === i.id);
              byIndex[idx] = {
                id: i.id,
                url: i.url,
                urlFull: full?.url,
              };
            }
          });
        setImagesByIndex(byIndex);
      })
      .catch(() => setImagesByIndex({}));
  }, [open, carTestId, initialFrameIndex]);

  const existingForFrame = imagesByIndex[currentFrameIndex];
  const hasCapturedForFrame = false; // Single-frame capture handled via onCapture
  const allSegmentsComplete = Array.from(
    { length: FRAMES_TOTAL },
    (_, i) => i,
  ).every((i) => imagesByIndex[i] != null);

  const handleRecordingComplete = useCallback((blob: Blob) => {
    setRecordedVideoBlob(blob);
    setPhase("trim");
  }, []);

  const handleFramesExtracted = useCallback(
    (frames: Blob[]) => {
      if (!carTestId) return;
      const capped = frames.slice(0, FRAMES_TOTAL);
      capped.forEach((blob, i) => {
        enqueue(i, "side", blob);
        setImagesByIndex((prev) => ({
          ...prev,
          [i]: {
            id: `pending-${i}`,
            url: URL.createObjectURL(blob),
          },
        }));
      });
      setRecordedVideoBlob(null);
      setPhase("picker");
    },
    [carTestId, enqueue],
  );

  const handleSegmentUpload = useCallback(
    async (frameIndex: number, file: File) => {
      const blob = await file
        .arrayBuffer()
        .then((b) => new Blob([b], { type: file.type }));
      enqueue(frameIndex, "side", blob);
      setImagesByIndex((prev) => ({
        ...prev,
        [frameIndex]: {
          id: `pending-${frameIndex}`,
          url: URL.createObjectURL(blob),
        },
      }));
    },
    [enqueue],
  );

  const handleOpenCamera = useCallback((frameIndex: number) => {
    setCurrentFrameIndex(frameIndex);
    setPhase("camera");
  }, []);

  const handleCapture = useCallback(
    (blob: Blob) => {
      enqueue(currentFrameIndex, "side", blob);
      setImagesByIndex((prev) => ({
        ...prev,
        [currentFrameIndex]: {
          id: `pending-${currentFrameIndex}`,
          url: URL.createObjectURL(blob),
        },
      }));
      setPhase("picker");
    },
    [currentFrameIndex, enqueue],
  );

  const handleBackToPicker = useCallback(() => {
    setPhase("picker");
  }, []);

  const handleCompleteAndClose = useCallback(() => {
    onOpenChange(false);
    setPhase("guide");
    setCurrentFrameIndex(0);
    setRecordedVideoBlob(null);
  }, [onOpenChange]);

  const handleDeleteFromPicker = useCallback(
    async (frameIndex: number, imageId: string) => {
      if (!carTestId || imageId.startsWith("pending-")) return;
      setDeletingFrameIndex(frameIndex);
      try {
        await removeCarTestImage({ carTestId, imageId });
        setImagesByIndex((prev) => {
          const next = { ...prev };
          delete next[frameIndex];
          return next;
        });
      } catch {
        // ignore
      } finally {
        setDeletingFrameIndex(null);
      }
    },
    [carTestId, removeCarTestImage],
  );

  const handleViewFullscreen = useCallback(
    (frameIndex: number, url: string, urlFull?: string) => {
      setFullscreenImage({ url: urlFull || url, index: frameIndex });
    },
    [],
  );

  const handleDeleteImage = useCallback(async () => {
    if (
      !carTestId ||
      !existingForFrame ||
      existingForFrame.id.startsWith("pending-")
    )
      return;
    try {
      await removeCarTestImage({ carTestId, imageId: existingForFrame.id });
      setImagesByIndex((prev) => {
        const next = { ...prev };
        delete next[currentFrameIndex];
        return next;
      });
      setPhase("picker");
    } catch {
      // ignore
    }
  }, [carTestId, currentFrameIndex, existingForFrame, removeCarTestImage]);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next && (phase === "video" || phase === "trim" || phase === "camera")) {
        if (!allDone && uploadTotal > 0) {
          const ok = window.confirm(labels.closeWithUploadWarning);
          if (ok) {
            onOpenChange(false);
            setPhase("guide");
            setRecordedVideoBlob(null);
          }
          return;
        }
      }
      onOpenChange(next);
      if (!next) {
        setPhase("guide");
        setRecordedVideoBlob(null);
      }
    },
    [allDone, uploadTotal, onOpenChange, phase, labels.closeWithUploadWarning],
  );

  const canComplete = allDone || uploadTotal === 0;

  // Notify parent when all uploads complete so it can refresh synchronously
  const prevAllDoneRef = React.useRef(false);
  React.useEffect(() => {
    if (allDone && !prevAllDoneRef.current && uploadTotal > 0) {
      onUploadsComplete?.();
    }
    prevAllDoneRef.current = allDone;
  }, [allDone, uploadTotal, onUploadsComplete]);

  const renderWithBold = (text: string) => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) =>
      part.startsWith("**") && part.endsWith("**")
        ? React.createElement(
            "strong",
            { key: i, className: "font-semibold" },
            part.slice(2, -2),
          )
        : part,
    );
  };
  const guideTips = (labels as { guideTips?: Record<string, string> }).guideTips ?? {};
  const tipItems = [
    { icon: Video, text: guideTips.videoRecord },
    { icon: Ruler, text: guideTips.distance },
    { icon: Move, text: guideTips.sameHeight },
    { icon: Sun, text: guideTips.lighting },
    { icon: CheckCircle2, text: guideTips.overlay },
    { icon: Camera, text: guideTips.landscape },
    { icon: RotateCcw, text: guideTips.order },
    { icon: Lightbulb, text: guideTips.steady },
  ].filter((t) => t.text);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg sm:max-w-xl max-h-[90dvh] overflow-hidden flex flex-col p-0 gap-0 border border-slate-200/60 dark:border-slate-700/60 shadow-2xl bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
        <div className="px-6 pt-6 pb-4">
          <DialogHeader className="space-y-1.5">
            <DialogTitle className="text-xl font-bold tracking-tight bg-gradient-to-r from-slate-800 to-slate-600 dark:from-slate-100 dark:to-slate-300 bg-clip-text text-transparent">
              {labels.modalTitle}
            </DialogTitle>
            <DialogDescription className="text-slate-600 dark:text-slate-400 text-sm">
              {labels.modalDescription}
            </DialogDescription>
          </DialogHeader>
        </div>

        {(phase === "guide" || phase === "video") && (
          <div className="flex-1 overflow-auto px-6 pb-6 space-y-5">
            <div className="rounded-xl bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm border border-slate-200/80 dark:border-slate-700/80 p-4 shadow-sm">
              <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-2 mb-3">
                <Lightbulb className="h-4 w-4 text-amber-500" />
                {labels.guideTitle}
              </p>
              <ul className="space-y-2.5">
                {tipItems.map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <li
                      key={i}
                      className="flex items-start gap-3 text-sm text-slate-600 dark:text-slate-300"
                    >
                      <span className="mt-0.5 shrink-0 rounded-full bg-primary/10 p-1">
                        <Icon className="h-3.5 w-3.5 text-primary" />
                      </span>
                      <span className="min-w-0 flex-1 leading-relaxed">
                        {renderWithBold(item.text)}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
            {!carTestId ? (
              <p className="text-sm text-destructive py-4">
                {labels.selectTestFirst}
              </p>
            ) : phase === "guide" ? (
              <Button
                onClick={() => setPhase("video")}
                className="w-full h-11 rounded-xl font-semibold"
                size="lg"
              >
                <Video className="h-4 w-4 mr-2" />
                {labels.startVideoRecording ?? "Starta videoinspelning"}
              </Button>
            ) : (
              <Exterior360VideoCaptureView
                onRecordingComplete={handleRecordingComplete}
                labels={{
                  startRecording: labels.startVideoRecording ?? "Starta inspelning",
                  stopRecording: labels.stopRecording ?? "Stoppa inspelning",
                  recording: labels.recording ?? "Inspelning",
                  cameraStarting: labels.cameraStarting,
                  cameraError: labels.cameraError,
                  landscapeHint: labels.videoOverlayHint ?? "Håll telefonen liggande. Håll hela bilen inom den vita rutan.",
                }}
              />
            )}
          </div>
        )}

        {phase === "trim" && recordedVideoBlob && (
          <div className="flex-1 overflow-auto px-6 pb-6">
            <Exterior360VideoTrimView
              videoBlob={recordedVideoBlob}
              onFramesExtracted={handleFramesExtracted}
              labels={{
                trimTitle: labels.trimTitle ?? "Trimma video",
                trimDescription: labels.trimDescription ?? "Välj start och slut om du vill klippa bort fel i början eller slutet.",
                trimGreenRedHint: labels.trimGreenRedHint,
                startTime: labels.trimStart ?? "Startpunkt",
                endTime: labels.trimEnd ?? "Slutpunkt",
                startHelp: labels.trimStartHelp ?? "Dra slidern – videon visar exakt var 360-inspelningen börjar",
                endHelp: labels.trimEndHelp ?? "Dra slidern – videon visar exakt var 360-inspelningen slutar",
                selectedLength: labels.trimSelectedLength ?? "Vald längd",
                trimPlayHint: labels.trimPlayHint ?? "Spela videon för att förhandsgranska · Dra sliderna för att sätta start/slut",
                extractingFrames: labels.extractingFrames ?? "Extraherar bildrutor",
                generatingTitle: labels.uploadGenerating ?? "Genererar 360-vy",
                generatingStep1Extract: labels.generatingStep1Extract,
                extractingCanTakeTime: labels.extractingCanTakeTime,
                confirmExtract: labels.confirmExtract ?? "Skapa 360-vy",
              }}
            />
            <Button
              variant="ghost"
              onClick={() => {
                setRecordedVideoBlob(null);
                setPhase("video");
              }}
              className="mt-4"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              {labels.backToPicker ?? "Tillbaka"}
            </Button>
          </div>
        )}

        {phase === "picker" && carTestId && (
          <div className="flex-1 overflow-auto px-6 pb-6 space-y-5">
            <Exterior360SegmentPicker
              imagesByIndex={imagesByIndex}
              labels={{
                selectOrTake: labels.segmentSelectOrTake,
                uploadImage: labels.segmentUploadImage,
                openCamera: labels.segmentOpenCamera,
                index: labels.segmentIndex,
                imageLabel: labels.segmentImageLabel,
                deleteImage: labels.deleteImage,
                viewFullscreen: labels.segmentViewFullscreen,
                segmentUploaded: labels.segmentUploaded,
                segmentEmpty: labels.segmentEmpty,
                segmentPartial: labels.segmentPartial ?? "Delvis",
                noImageFound: labels.noImageFound,
                groupFrames: labels.groupFrames ?? "Rutor",
              }}
              onUpload={handleSegmentUpload}
              onOpenCamera={handleOpenCamera}
              onDelete={handleDeleteFromPicker}
              onViewFullscreen={handleViewFullscreen}
              uploadingFrameIndices={uploadingFrameIndices}
              deletingFrameIndices={
                deletingFrameIndex != null ? [deletingFrameIndex] : []
              }
              uploadProgress={
                uploadTotal > 0 && !allDone
                  ? { done: doneCount, total: uploadTotal, failed: failedCount }
                  : undefined
              }
              uploadProgressLabels={{
                generating: labels.uploadGenerating,
                progressPercent: labels.uploadProgressPercent,
                timeEstimate: labels.uploadTimeEstimate,
                complete: labels.uploadComplete,
              }}
              transparent
            />
            {uploadTotal > 0 && (
              <div className="space-y-1">
                <p className="text-sm font-medium text-foreground">
                  {allDone
                    ? (labels.uploadComplete ?? "Uppladdning klar")
                    : `${labels.uploadProgress ?? "Uppladdning"}: ${uploadTotal > 0 ? Math.round((doneCount / uploadTotal) * 100) : 0}%`}
                  {failedCount > 0 && (
                    <span className="text-destructive ml-1">
                      ({failedCount} {labels.uploadFailed})
                    </span>
                  )}
                </p>
                {!allDone && uploadTotal >= 5 && (
                  <p className="text-xs text-muted-foreground">
                    {labels.uploadTimeEstimate ?? "Kan ta upp till 1–2 minuter beroende på nätverkshastighet."}
                  </p>
                )}
              </div>
            )}
            {(allSegmentsComplete && canComplete) && (
              <Button
                onClick={handleCompleteAndClose}
                size="lg"
                className="w-full h-11 rounded-xl font-semibold"
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                {labels.done ?? "Klart – stäng"}
              </Button>
            )}
          </div>
        )}

        {phase === "camera" && carTestId && (
          <div className="flex-1 min-h-0 overflow-auto px-6 pb-4">
            <Exterior360CameraView
              labels={labels}
              viewType="side"
              frameIndex={currentFrameIndex}
              totalFrames={FRAMES_TOTAL}
              capturedBlob={null}
              existingImageUrl={existingForFrame?.url}
              allSegmentsComplete={allSegmentsComplete}
              onCapture={handleCapture}
              onBack={handleBackToPicker}
              onDelete={existingForFrame ? handleDeleteImage : undefined}
              onComplete={handleCompleteAndClose}
              canComplete={canComplete}
              totalSteps={FRAMES_TOTAL}
              currentStepIndex={currentFrameIndex}
              uploadStatus={
                uploadTotal > 0
                  ? { done: doneCount, total: uploadTotal, failed: failedCount }
                  : undefined
              }
            />
            {failedCount > 0 && (
              <p className="text-sm text-destructive mt-3 px-3 py-2 rounded-lg bg-destructive/10">
                {failedCount} {labels.uploadFailed}
              </p>
            )}
          </div>
        )}
      </DialogContent>

      <Dialog
        open={!!fullscreenImage}
        onOpenChange={(open) => !open && setFullscreenImage(null)}
      >
        <DialogContent className="max-w-[95vw] max-h-[95dvh] w-auto p-2 bg-black/95 border-none">
          {fullscreenImage && (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-2 top-2 z-50 text-white hover:bg-white/20"
                onClick={() => setFullscreenImage(null)}
                aria-label={labels.exitFullscreen}
              >
                <X className="h-5 w-5" />
              </Button>
              <img
                src={fullscreenImage.url}
                alt=""
                className="max-w-full max-h-[90dvh] object-contain"
              />
              <p className="text-center text-sm text-slate-400 mt-1">
                {labels.segmentImageLabel ?? "Bild"} {fullscreenImage.index + 1}
              </p>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
